import React, { useState, useCallback, Dispatch, SetStateAction, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Popper,
  Stack,
  Tooltip,
  ClickAwayListener,
  Grow,
  Paper,
  MenuList
} from '@mui/material';
import {
  Cached as CachedIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { classes } from '../styles';
import { classes as aiStudioClasses } from '../../AIStudioModal/styles';
import { modalSteps } from '../types';
import { StyledSnackbarProps } from '../../../NewUI/Components/CustomUIElements/StyledSnackbar';
import { IWebsocketData } from '../../SharedComponents/types';
import { convertToLocalEnglish } from '../../../NewUI/Components/Utilities/convertToLocalEnglish';
import {
  arrayQuestion,
  questionStringToArray
} from '../../SharedComponents/Utility/questionStringToArray';
import SelectableCopyableQuestions from '../../SharedComponents/SelectableCopyableQuestions';
import { handleAPIRequest } from '../../../NewUI/Components/Utilities/handleAPIRequest';
import FollowUpEmailModal from './FollowUpEmail/FollowUpEmail';
import { DynamicFooterWithInput } from '../../SharedComponents/Footers/DynamicFoorterWithInput';
import { LoadingOverlay } from '../../AIStudioModal/components/LoadingOverlay';
import DynamicFooter from '../../SharedComponents/Footers/DynamicFooter';
import { actionsMenuPaperCommonStyles } from '../../../NewUI/shared/commonStyles';
import { theme } from '../../../ThemeContext/ThemeObject';

const convertedQuestionData = (questions: string) => {
  const convertedQUestions = convertToLocalEnglish(questions || '');
  return questionStringToArray(convertedQUestions);
};

const SUGGESTED_PROMPTS = [
  'Ask about relevant experience',
  'Generate a situational judgement question',
  'Ask a question about soft skills'
];

const ResponseStep = ({
  setStep,
  requestQuestions,
  handleFeedbackRequest,
  questionData,
  isNotification,
  jobId,
  selectedApplicantId,
  setSnackbarState,
  newQuestionsData,
  hasFeedback = false,
  isLoading = false,
  questionsMaxHeight
}: {
  setStep?: Dispatch<SetStateAction<modalSteps>>;
  requestQuestions?: (feedbackInput?: string) => void;
  handleFeedbackRequest?: (feedbackInput?: string) => void;
  questionData: IWebsocketData | null;
  isNotification?: boolean;
  jobId?: number;
  selectedApplicantId?: number | null;
  setSnackbarState: StyledSnackbarProps['setSnackbarState'];
  newQuestionsData?: IWebsocketData | null;
  hasFeedback?: boolean;
  isLoading?: boolean;
  questionsMaxHeight?: string;
}) => {
  const [open, setOpen] = useState(false);
  const prevOpen = useRef(open);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [questions, setQuestions] = useState<arrayQuestion[]>(
    convertedQuestionData(questionData?.content || '')
  );
  const [newQuestions, setNewQuestions] = useState<arrayQuestion[]>(
    convertedQuestionData(newQuestionsData?.content || '')
  );

  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(false);
  const saveSelectedQuestions = useCallback(async () => {
    const sendableQuestions = questions.filter((item) => item.selected).map((item) => item.text);
    setQuestions((prev) =>
      prev.map((item) => (item.selected ? { ...item, status: 'loading' } : item))
    );
    handleAPIRequest({
      url: `/api/v4/jobs/${jobId || questionData?.job_id}/applications/${
        selectedApplicantId || questionData?.application_id
      }/saved_interview_questions/add_questions`,
      method: 'PATCH',
      body: {
        saved_interview_questions: {
          questions: sendableQuestions
        }
      },
      onSuccess: () => {
        setQuestions((prev) =>
          prev.map((item) => (item.selected ? { ...item, status: 'sent', selected: false } : item))
        );
      },
      onFailure: () => {
        setQuestions((prev) =>
          prev.map((item) => (item.selected ? { ...item, status: 'error', selected: false } : item))
        );
      }
    });
  }, [jobId, questionData?.application_id, questionData?.job_id, questions, selectedApplicantId]);

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) return;
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (questionData?.content) {
      setQuestions(convertedQuestionData(questionData.content));
    }
  }, [questionData?.content]);

  useEffect(() => {
    if (newQuestionsData?.content) {
      setNewQuestions(convertedQuestionData(newQuestionsData.content));
    }
  }, [newQuestionsData?.content]);

  return (
    <>
      <Box sx={classes.responseHeader}>
        <Box sx={classes.header}>Suggested interview questions</Box>
        {!isNotification && requestQuestions && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip placement="top" title={'More'} arrow>
              <IconButton
                id={`more`}
                disabled={isLoading}
                onClick={handleToggle}
                ref={anchorRef}
                sx={aiStudioClasses.actionButton}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>

            <Tooltip placement="top" title={'Generate email'} arrow>
              <IconButton
                id={`generate-email-button`}
                disabled={isLoading || !questions.some((item) => item.selected)}
                onClick={() => {
                  setEmailModalOpen(true);
                }}
                sx={aiStudioClasses.actionButton}
                size="small"
              >
                <EmailIcon />
              </IconButton>
            </Tooltip>

            <Tooltip placement="top" title={'Regenerate'} arrow>
              <IconButton
                id={`regenerate`}
                disabled={isLoading}
                onClick={() => handleFeedbackRequest?.('')}
                sx={aiStudioClasses.actionButton}
                size="small"
              >
                <CachedIcon />
              </IconButton>
            </Tooltip>

            <Button
              id="save-questions-button"
              sx={{ ...aiStudioClasses.footerContinueButton, minWidth: '146px' }}
              onClick={saveSelectedQuestions}
              disabled={isLoading || !questions.some((item) => item.selected)}
            >
              Save questions
            </Button>
          </Stack>
        )}
      </Box>
      <Box sx={classes.applicantNameHeader}>
        <a
          href={`/admin/jobs/${jobId || questionData?.job_id}/applications/${
            selectedApplicantId || questionData?.application_id
          }`}
          target="_blank"
          rel="noreferrer"
        >
          {questionData?.candidate_name}
        </a>
      </Box>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <SelectableCopyableQuestions
          questions={questions}
          setQuestions={setQuestions}
          maxHeight={questionsMaxHeight}
          hasFeedback={hasFeedback}
          newQuestions={newQuestions}
          isLoading={isLoading}
        />
        <LoadingOverlay isLoading={isLoading} generationType="interview" showSocketMessage />
      </Box>

      {!isNotification && (
        <DynamicFooterWithInput
          isLoading={isLoading}
          submitCallback={(value) => handleFeedbackRequest?.(value)}
          description={
            <>
              Here are some follow up questions, you can use them during an <span>interview</span>,{' '}
              <span>email</span> them to your candidate or save them to view later.
            </>
          }
          suggestedPrompts={SUGGESTED_PROMPTS}
        />
      )}

      {isNotification && (
        <DynamicFooter
          primaryButtonText="Save questions"
          primaryButtonID="save-questions-button"
          handlePrimaryButtonClick={saveSelectedQuestions}
          disablePrimaryButton={!questions.some((item) => item.selected)}
          secondaryButtonText="Generate email"
          secondaryButtonID="generate-email-button"
          handleSecondaryButtonClick={() => setEmailModalOpen(true)}
          disableSecondaryButton={!questions.some((item) => item.selected)}
          felixMessage={
            <>
              Here are some follow up questions, you can use them during an <span>interview</span>,{' '}
              <span>email</span> them to your candidate or save them to view later.
            </>
          }
          felixMessageMaxWidth="420px"
          alternateSecondaryButton={true}
        />
      )}

      {emailModalOpen && (
        <FollowUpEmailModal
          questions={questions.filter((item) => item.selected).map((item) => item.text)}
          modalOpen={emailModalOpen}
          setModalOpen={setEmailModalOpen}
          jobId={jobId || questionData?.job_id || 0}
          applicationId={selectedApplicantId || questionData?.application_id || 0}
          setSnackbarState={setSnackbarState}
        />
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        disablePortal
        sx={{
          '& .MuiPaper-root': {
            ...actionsMenuPaperCommonStyles,
            padding: 0,
            marginTop: theme.spacing(1.5)
          },
          '& .MuiMenuItem-root': {
            fontFamily: 'Source Sans Pro',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#666666',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'transparent',
              color: theme.palette.primary.deepBlue
            }
          }
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                >
                  <MenuItem
                    onClick={() => {
                      setStep?.('input');
                    }}
                  >
                    Start again
                  </MenuItem>
                  <MenuItem onClick={() => setStep && setStep('saved')}>
                    View saved questions
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default ResponseStep;
